// NPM Imports
import * as yaml from "js-yaml";
import { Buffer } from "buffer/";

// Internal Imports
import { get, put, post, getForFileAsResponse } from "../../api/index";
import { API_ENDPOINTS, GRID } from "../../constants";
import { GlobalUtils } from "../../utils";

// Method Imports
import {
  parseAppFlags,
  getUsageEventsBasedOnPricingPlan,
} from "./commonMethods";

// Destructuring
const { PAGE_TO_APPVIEW_MAP } = GRID;
const { APIS } = API_ENDPOINTS;
const { readDataFromLocalStorage, writeDataToLocalStorage } = GlobalUtils;
export const fetchAppConfig = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.APP_VIEW_CONFIG}`)
      .then(res => {
        writeDataToLocalStorage("appView", res.App);
        resolve(res);
      })
      .catch(error => {
        console.error(error);
        reject(error);
      });
  });

export const fetchFlagConfig = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.ORGS}/flags`)
      .then(res => {
        const flagsObj = parseAppFlags(res.OrgFlags || []);
        writeDataToLocalStorage("appFlag", flagsObj);
        resolve(res);
      })
      .catch(error => {
        console.error(error);
        reject(error);
      });
  });

export const fetchUserSignUpInfo = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.AUTH}/signup_info`)
      .then(res => {
        writeDataToLocalStorage("userSignUpInfo", res.SignupInfo);
        resolve(res);
      })
      .catch(error => {
        console.error(error);
        reject(error);
      });
  });

export const getRbacRoles = async () => {
  const rbacRoles = await get(`${APIS.RBAC}/roles`)
    .then(res => res)
    .catch(error => {
      throw error;
    });
  return rbacRoles;
};

export const getColVisibilityList = async page => {
  const getAppView = readDataFromLocalStorage("appView");
  const view =
    getAppView && getAppView.views
      ? getAppView.views[PAGE_TO_APPVIEW_MAP[page]]
      : null;
  const colList = await get(`${APIS.RBAC}/column_visibility/${view}/`)
    .then(res => res)
    .catch(error => {
      throw error;
    });
  return colList;
};

export const updateColVisibilityList = async (payload, page) => {
  const getAppView = readDataFromLocalStorage("appView");
  const view =
    getAppView && getAppView.views
      ? getAppView.views[PAGE_TO_APPVIEW_MAP[page]]
      : null;
  const colList = await put(`${APIS.RBAC}/column_visibility/${view}/`, payload)
    .then(res => res)
    .catch(error => {
      throw error;
    });
  return colList;
};

export const checkInboxUnreadNotificationService = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.GET_NOTIFICATIONS}/unseen`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error(error);
        reject(error);
      });
  });

export const sendFeatureRequest = data =>
  new Promise((resolve, reject) => {
    post(`${APIS.SUPPORT_EMAIL}`, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getMeterUsageService = entity =>
  new Promise((resolve, reject) => {
    get(`${APIS.ORGS_METERS}?entity=${entity}&promotional_included=false`)
      .then(res => {
        // * Store entity map in local storage
        const meteredFeatureMap =
          res && res.meters && res.meters.feature_entity_mapping;
        delete meteredFeatureMap["quoting.pricebook"];
        delete meteredFeatureMap["wf.standard_variables"];
        delete meteredFeatureMap["wf.system.user_management"];
        delete meteredFeatureMap["documents.permalink"];
        delete meteredFeatureMap["documents.snaplink"];
        writeDataToLocalStorage("featureEntityMap", meteredFeatureMap);

        // * Return the metered counter value
        const usage =
          res &&
          res.meters &&
          res.meters.entity_usage_meter &&
          res.meters.entity_usage_meter[entity];

        resolve(usage);
      })
      .catch(error => {
        reject(error);
      });
  });

export const apiPing = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.PING}`)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });

export const fetchPricingInfo = () =>
  new Promise((resolve, reject) => {
    getForFileAsResponse(`${process.env.REACT_APP_PRICING_YAML_URL}`)
      .then(res => {
        const doc = yaml.load(Buffer.from(res).toString("utf8"));
        resolve(doc);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getDocCtaEventsService = ({ eventNames, isFreePlan }) =>
  new Promise((resolve, reject) => {
    const range = getUsageEventsBasedOnPricingPlan(isFreePlan);

    get(
      `${APIS.EVENTS_AGGREGATE}?from=${range.firstDay}&to=${range.lastDay}&event=${eventNames}`,
    )
      .then(res => {
        resolve(res.data);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getDigitalTransationEventsService = ({ docId, isFreePlan }) =>
  new Promise((resolve, reject) => {
    const range = getUsageEventsBasedOnPricingPlan(isFreePlan);

    get(
      `${APIS.EVENTS_DIGITAL_TRANSATION}?from=${range.firstDay}&to=${range.lastDay}&document_id=${docId}`,
    )
      .then(res => {
        resolve(res.data);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getAllMeteredUsage = () =>
  new Promise((resolve, reject) => {
    get(`${APIS.ORGS_METERS}?promotional_included=false`)
      .then(res => {
        // * Return the metered counter value
        const usage = res && res.meters && res.meters.entity_usage_meter;
        resolve(usage);
      })
      .catch(error => {
        reject(error);
      });
  });

export const eventsTrackingApi = eventName =>
  new Promise((resolve, reject) => {
    // todo: remove this temp if check once event names are added for the upgrade popup
    if (eventName) {
      const payload = { source: "React_FE", name: eventName };
      post(`${APIS.EVENTS}`, payload)
        .then(res => {
          resolve(res);
        })
        .catch(error => {
          console.error(`commonService => eventsTrackingApi => ${error}`);
          reject(error);
        });
    }
    resolve();
  });

export const getDriftAvatarImage = appId =>
  new Promise((resolve, reject) => {
    fetch(`https://embeds.driftcdn.com/embeds/${appId}.json`)
      .then(res => res.json())
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });
